import React from 'react';

import type { HubItem } from '../api/types';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { buildArticlePath, buildNewsPath } from '../utils/contentPaths';

const formatDate = (iso: string): string => {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

interface HubItemRowProps {
  item: HubItem;
  /**
   * False renders the same row as a plain element instead of a link — for the admin preview,
   * where navigating away from the dialog would be wrong. The row looks identical either way,
   * which is the point: the preview shows the real thing, not a lookalike.
   */
  interactive?: boolean;
}

/**
 * One listing entry for both surfaces. An article and a news item carry the same fields, so the
 * feed and the library differ in how they're *arranged* (by date vs. by category), not in how a
 * single entry looks.
 *
 * WAS `HubItemCard`, and the rename is the point: this is a keyed row on a sheet, not a card.
 * DESIGN.md's No-Card Rule is explicit — "a region is bounded by a rule or by nothing" — and
 * the previous version was a rounded, bordered, shadow-on-hover box, which is the shape the
 * whole system was built to refuse. Structure is now a single hairline between rows.
 */
const HubItemRow: React.FC<HubItemRowProps> = ({ item, interactive = true }) => {
  const meta = [formatDate(item.date), item.categoryTitle].filter(Boolean).join(' · ');
  const className = 'group grid grid-cols-[7rem_1fr_auto] items-start gap-4 py-5 sm:gap-6';

  const body = (
    <>
      {/* A plate window in miniature: declared aspect, hairline frame, cut into the sheet
          rather than floated on it. The registration ticks the full-size plate carries are
          left off at this scale — at 112px wide they read as dirt on the sheet, not as
          survey marks. An absent picture reserves the same box on the sunk ground, so a
          listing with and without art holds the same rhythm. */}
      <div className="aspect-[4/3] w-full overflow-hidden border border-rule-strong bg-page-sunk">
        {item.pictureUrl && (
          <img src={item.pictureUrl} alt="" className="h-full w-full object-cover" />
        )}
      </div>

      <div className="min-w-0">
        <p className="font-ui text-step-0 text-ink">
          {item.title}
          {item.externalUrl && (
            <ExternalLink
              size={13}
              aria-hidden="true"
              className="ml-1.5 inline align-[-2px] text-ink-muted"
            />
          )}
        </p>
        {meta && <p className="mt-1 font-ui text-step--1 text-ink-muted">{meta}</p>}
        {item.summary && (
          <p className="mt-2 line-clamp-2 text-step--1 text-ink-muted">{item.summary}</p>
        )}
      </div>

      <ArrowRight
        size={16}
        aria-hidden="true"
        className="mt-1 shrink-0 text-ink-muted transition-transform duration-fast ease-sheet group-hover:translate-x-0.5"
      />
    </>
  );

  if (!interactive) {
    return <div className={className}>{body}</div>;
  }

  // Always route through the hub, even for an item that lives elsewhere: the reading view is
  // what sends the reader out, so the link-out stays visible in one place.
  const to =
    item.kind === 'article'
      ? buildArticlePath(item.slug || item.id)
      : buildNewsPath(item.slug || item.id);

  return (
    <NavLink to={to} className={className}>
      {body}
    </NavLink>
  );
};

export default HubItemRow;
